/**
 * Movement patterns as composable primitives.
 *
 * A `MovementPattern` is a pure function from (state, square, piece) to the
 * geometric pseudo-legal destinations of that piece — respecting board edges,
 * blocking, and friendly pieces, but NOT higher-level rules like "you may not
 * make a move that leaves your own king in check". Those rules are applied
 * centrally in moves.ts so individual pieces stay simple. New piece behaviours
 * are added by composing `slider`/`leaper`/custom functions here.
 */
import { forwardDir, fromSquare, shift, toSquare } from "./coords";
import type { GameState, Piece, Square, Vector } from "./types";

export interface RawMove {
  readonly to: Square;
  readonly capture: boolean;
}

export interface MoveContext {
  readonly state: GameState;
  readonly from: Square;
  readonly piece: Piece;
}

export type MovementPattern = (ctx: MoveContext) => RawMove[];

// Direction sets reused across pieces.
export const ORTHOGONAL: readonly Vector[] = [
  [1, 0],
  [-1, 0],
  [0, 1],
  [0, -1],
];
export const DIAGONAL: readonly Vector[] = [
  [1, 1],
  [1, -1],
  [-1, 1],
  [-1, -1],
];
export const ALL_DIRECTIONS: readonly Vector[] = [...ORTHOGONAL, ...DIAGONAL];

export const KNIGHT_OFFSETS: readonly Vector[] = [
  [1, 2],
  [2, 1],
  [-1, 2],
  [-2, 1],
  [1, -2],
  [2, -1],
  [-1, -2],
  [-2, -1],
];

/**
 * The Camel's leaps: an extended knight (a (3,1) leaper). Reaches one square
 * further than the Knight in the long direction, jumping over anything between.
 */
export const CAMEL_OFFSETS: readonly Vector[] = [
  [1, 3],
  [3, 1],
  [-1, 3],
  [-3, 1],
  [1, -3],
  [3, -1],
  [-1, -3],
  [-3, -1],
];

/** The eight squares exactly two steps away along a rank, file, or diagonal. */
export const DISTANCE_TWO: readonly Vector[] = [
  [2, 0],
  [-2, 0],
  [0, 2],
  [0, -2],
  [2, 2],
  [2, -2],
  [-2, 2],
  [-2, -2],
];

function occupant(state: GameState, sq: Square): Piece | null {
  return state.board[sq] ?? null;
}

/** A piece that can never move (immovable terrain, e.g. the Hill). */
export const noMovement: MovementPattern = () => [];

/** A ranged piece that slides along directions until blocked. */
export function slider(directions: readonly Vector[]): MovementPattern {
  return ({ state, from, piece }) => {
    const moves: RawMove[] = [];
    for (const dir of directions) {
      let sq: Square | null = from;
      while (true) {
        sq = shift(sq, dir, state.config);
        if (sq === null) break;
        const target = occupant(state, sq);
        if (!target) {
          moves.push({ to: sq, capture: false });
          continue;
        }
        if (target.owner !== piece.owner) moves.push({ to: sq, capture: true });
        break; // any piece blocks further travel
      }
    }
    return moves;
  };
}

/** A piece that jumps to a fixed set of offsets (knight, king step, prince). */
export function leaper(offsets: readonly Vector[]): MovementPattern {
  return ({ state, from, piece }) => {
    const moves: RawMove[] = [];
    for (const off of offsets) {
      const sq = shift(from, off, state.config);
      if (sq === null) continue;
      const target = occupant(state, sq);
      if (!target) moves.push({ to: sq, capture: false });
      else if (target.owner !== piece.owner) moves.push({ to: sq, capture: true });
    }
    return moves;
  };
}

/**
 * Pawn movement: forward one (or two from its home rank if unmoved), and
 * diagonal-forward captures. En passant is intentionally omitted. Promotion is
 * resolved by the engine when a pawn reaches the far rank.
 */
export const pawnMovement: MovementPattern = ({ state, from, piece }) => {
  const cfg = state.config;
  const dir = forwardDir(piece.owner);
  const { file, rank } = fromSquare(from, cfg.width);
  const moves: RawMove[] = [];

  // Forward one.
  const oneRank = rank + dir;
  if (oneRank >= 0 && oneRank < cfg.height) {
    const one = toSquare(file, oneRank, cfg.width);
    if (!occupant(state, one)) {
      moves.push({ to: one, capture: false });
      // Forward two from the starting rank, if both squares are clear.
      if (!piece.hasMoved) {
        const twoRank = rank + dir * 2;
        if (twoRank >= 0 && twoRank < cfg.height) {
          const two = toSquare(file, twoRank, cfg.width);
          if (!occupant(state, two)) moves.push({ to: two, capture: false });
        }
      }
    }
  }

  // Diagonal captures.
  for (const df of [-1, 1]) {
    const sq = shift(from, [df, dir], cfg);
    if (sq === null) continue;
    const target = occupant(state, sq);
    if (target && target.owner !== piece.owner) moves.push({ to: sq, capture: true });
  }

  return moves;
};

/**
 * A slider whose travel directions are relative to the owner's forward facing:
 * each `df` is paired with the owner's forward rank step. Used by the Lance,
 * which slides diagonally forward only and can never retreat.
 */
export function forwardSlider(fileDeltas: readonly number[]): MovementPattern {
  return ({ state, from, piece }) => {
    const dir = forwardDir(piece.owner);
    const moves: RawMove[] = [];
    for (const df of fileDeltas) {
      let sq: Square | null = from;
      while (true) {
        sq = shift(sq, [df, dir], state.config);
        if (sq === null) break;
        const target = occupant(state, sq);
        if (!target) {
          moves.push({ to: sq, capture: false });
          continue;
        }
        if (target.owner !== piece.owner) moves.push({ to: sq, capture: true });
        break;
      }
    }
    return moves;
  };
}

/**
 * A leaper with separate geometries for moving and capturing: it relocates
 * (without capturing) to any empty `moveOffsets` square, and captures only on
 * `captureOffsets` squares. Used by the Outpost, which hops two squares to
 * reposition but can only strike an adjacent enemy.
 */
export function moveCaptureLeaper(
  moveOffsets: readonly Vector[],
  captureOffsets: readonly Vector[],
): MovementPattern {
  return ({ state, from, piece }) => {
    const moves: RawMove[] = [];
    for (const off of moveOffsets) {
      const sq = shift(from, off, state.config);
      if (sq !== null && !occupant(state, sq)) moves.push({ to: sq, capture: false });
    }
    for (const off of captureOffsets) {
      const sq = shift(from, off, state.config);
      if (sq === null) continue;
      const target = occupant(state, sq);
      if (target && target.owner !== piece.owner) moves.push({ to: sq, capture: true });
    }
    return moves;
  };
}

/**
 * Farmer movement — the inverse of a pawn: it steps diagonally forward to an
 * empty square (no double-step) and captures straight ahead. Promotion is
 * resolved by the engine when it reaches the far rank.
 */
export const farmerMovement: MovementPattern = ({ state, from, piece }) => {
  const cfg = state.config;
  const dir = forwardDir(piece.owner);
  const moves: RawMove[] = [];

  // Diagonal-forward moves (non-capturing).
  for (const df of [-1, 1]) {
    const sq = shift(from, [df, dir], cfg);
    if (sq !== null && !occupant(state, sq)) moves.push({ to: sq, capture: false });
  }

  // Straight-ahead capture.
  const ahead = shift(from, [0, dir], cfg);
  if (ahead !== null) {
    const target = occupant(state, ahead);
    if (target && target.owner !== piece.owner) moves.push({ to: ahead, capture: true });
  }

  return moves;
};
